import browserStorage from 'store';
import { Auth0Profile } from './AuthService';
import { AuthGenerationRecord } from './sessionTypes';

export const ACCESS_TOKEN_KEY = 'accessToken';
export const EXPIRES_AT_KEY = 'expiresAt';
export const SCOPE_KEY = 'scope';
export const PROFILE_KEY = 'profile';
export const ISSUED_AT_KEY = 'issuedAt';
export const GENERATION_ID_KEY = 'generationId';
export const GENERATION_RECORD_KEY = 'authSessionGeneration';

export interface SessionStorageLike {
  get(key: string): unknown;
  set(key: string, value: unknown): void;
  remove(key: string): void;
}

export interface StoredGenerationRecord {
  accessToken: string;
  scope: string;
  profile: Auth0Profile | null;
  expiresAt: number;
  issuedAt: number | null;
  generationId: string | null;
}

const toFiniteNumber = (value: unknown): number | null => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const parseGenerationRecord = (value: unknown): StoredGenerationRecord | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Partial<StoredGenerationRecord>;
  const expiresAt = toFiniteNumber(record.expiresAt);
  if (
    typeof record.accessToken !== 'string' ||
    !record.accessToken ||
    typeof record.scope !== 'string' ||
    !record.scope ||
    expiresAt === null
  ) {
    return null;
  }

  return {
    accessToken: record.accessToken,
    scope: record.scope,
    profile: record.profile || null,
    expiresAt,
    issuedAt: toFiniteNumber(record.issuedAt),
    generationId: typeof record.generationId === 'string' ? record.generationId : null,
  };
};

const readLegacyGenerationRecord = (storage: SessionStorageLike): StoredGenerationRecord | null => {
  const accessToken = storage.get(ACCESS_TOKEN_KEY);
  const scope = storage.get(SCOPE_KEY);
  const expiresAt = toFiniteNumber(storage.get(EXPIRES_AT_KEY));

  if (typeof accessToken !== 'string' || !accessToken || typeof scope !== 'string' || !scope || expiresAt === null) {
    return null;
  }

  const profile = (storage.get(PROFILE_KEY) as Auth0Profile | null) || null;

  return {
    accessToken,
    scope,
    profile,
    expiresAt,
    issuedAt: toFiniteNumber(storage.get(ISSUED_AT_KEY)),
    generationId:
      typeof storage.get(GENERATION_ID_KEY) === 'string' ? (storage.get(GENERATION_ID_KEY) as string) : null,
  };
};

const clearLegacyGenerationRecord = (storage: SessionStorageLike): void => {
  storage.remove(ACCESS_TOKEN_KEY);
  storage.remove(EXPIRES_AT_KEY);
  storage.remove(SCOPE_KEY);
  storage.remove(PROFILE_KEY);
  storage.remove(ISSUED_AT_KEY);
  storage.remove(GENERATION_ID_KEY);
};

export const readGenerationRecord = (storage: SessionStorageLike = browserStorage): StoredGenerationRecord | null => {
  const record = parseGenerationRecord(storage.get(GENERATION_RECORD_KEY));
  if (record) {
    return record;
  }

  const legacyRecord = readLegacyGenerationRecord(storage);
  if (!legacyRecord) {
    return null;
  }

  storage.set(GENERATION_RECORD_KEY, legacyRecord);
  clearLegacyGenerationRecord(storage);
  return legacyRecord;
};

export const writeGenerationRecord = (
  record: AuthGenerationRecord,
  storage: SessionStorageLike = browserStorage
): void => {
  storage.set(GENERATION_RECORD_KEY, record);
  clearLegacyGenerationRecord(storage);
};

export const clearGenerationRecord = (storage: SessionStorageLike = browserStorage): void => {
  storage.remove(GENERATION_RECORD_KEY);
  clearLegacyGenerationRecord(storage);
};

export const isNewerUnexpiredGeneration = (
  candidate: StoredGenerationRecord | AuthGenerationRecord | null,
  current: Pick<StoredGenerationRecord, 'generationId' | 'issuedAt'> | null,
  now: number
): boolean => {
  if (!candidate?.generationId || candidate.expiresAt <= now) {
    return false;
  }

  if (!current?.generationId) {
    return true;
  }
  if (candidate.generationId === current.generationId || candidate.issuedAt === null) {
    return false;
  }
  if (current.issuedAt === null || candidate.issuedAt > current.issuedAt) {
    return true;
  }
  if (candidate.issuedAt < current.issuedAt) {
    return false;
  }
  return candidate.generationId > current.generationId;
};
